import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './tournaments.css';
// Removed imageMap dependency - now using dynamic game heroUrl
import { formatDate, formatPrize } from '../../utils/formatters';
import ShareModal from './ShareModal';
import NotifyModal from './NotifyModal';
import { API_URL } from '../../config';


export default function TournamentCard({ data, compact = false, gamesData = [] }) {
  const [openShare, setOpenShare] = useState(false);
  const [openNotify, setOpenNotify] = useState(false);

  // ✅ OPTIMIERT: Kein API-Call mehr! Nutzt gamesData Props
  const gameHeroUrl = useMemo(() => {
    if (!data.game || !gamesData.length) return null;
    
    const game = gamesData.find(g => 
      g.gameKey === data.game || 
      g.title === data.game || 
      g.name === data.game
    );
    
    return game?.heroUrl || null;
  }, [data.game, gamesData]);

  const cover = useMemo(() => {
    // Priority: Tournament specific image > Game heroUrl > Default fallback
    if (data.heroUrl) return data.heroUrl;
    if (gameHeroUrl) return gameHeroUrl;
    
    // Simple fallback without imageMap dependency
    return 'https://via.placeholder.com/400x225/1a1a2e/ffffff?text=Tournament';
  }, [data.heroUrl, gameHeroUrl]);

  const dateText = formatDate(data.startAt);
  const shareMessage = `🎮 ${data.title}

📅 ${dateText}
💰 Prize Pool: ${formatPrize(data.prizePool)}
🎯 Format: ${data.format || 'TBD'}
🕹️ Platform: ${data.platform || 'TBD'}
🎪 Organizer: ${data.organizer || 'VersusArena'}

Ready to compete? Join the ultimate gaming tournament experience!`;

  return (
    <>
      <article className={`t-card ${compact ? 't-card--compact' : ''}`}>
        <div className="t-card__media">
          <img 
            src={cover} 
            alt={data.title} 
            loading="lazy"
            onError={(e) => {
              console.warn('Tournament cover failed to load:', cover);
              // Try simple fallback
              const fallback = 'https://via.placeholder.com/400x225/0d1321/ffffff?text=No+Image';
              if (e.target.src !== fallback) {
                e.target.src = fallback;
              } else {
                // Fallback also failed, hide image
                e.target.style.display = 'none';
                e.target.parentElement?.classList.add('image-failed');
              }
            }}
            onLoad={(e) => {
              e.target.style.opacity = '1';
              e.target.classList.add('loaded');
            }}
            style={{ opacity: 0, transition: 'opacity 0.4s ease' }}
          />
          {(
            data.status === 'COMING_SOON' || 
            data.status === 'coming_soon' || 
            data.status === 'upcoming' ||
            data.status === 'UPCOMING'
          ) && (
            <span className="t-card__badge-status">COMING SOON</span>
          )}
          <span
            className={
              't-card__badge-org ' +
              (data.organizer?.toLowerCase() === 'versus' ||
              data.organizer?.toLowerCase() === 'versusarena'
                ? 't-card__badge-org--versus'
                : 't-card__badge-org--partner')
            }
          >
            {data.organizer || 'Versus Arena'}
          </span>
        </div>

        <div className="t-card__body">
          <h3 className="t-card__title">{data.title}</h3>

          <div className="t-card__meta-row">
            <div className="t-meta">
              <strong>Date:</strong> {dateText}
            </div>
          </div>

          <div className="t-card__meta-row">
            <div className="t-meta">
              <strong>Prize Pool:</strong> {formatPrize(data.prizePool)}
            </div>
            <div className="t-meta">
              <strong>Format:</strong> {(data.format || 'TBD').replace(/_/g, ' ')}
            </div>
          </div>

          <div className="t-card__meta-row">
            <div className="t-meta">
              <strong>Platform:</strong> {data.platform || 'TBD'}
            </div>
          </div>
        </div>

        <div className="t-card__footer">
          {/* Status-based Button Logic */}
          {data.status === 'coming_soon' || data.status === 'COMING_SOON' ? (
            // Coming Soon: Only show Notify button
            <button
              className="va-btn va-btn--primary"
              onClick={() => setOpenNotify(true)}
            >
              🔔 Notify Me
            </button>
          ) : (
            // All other statuses: Show Details button that opens modal
            <Link
              to={`/tournaments/${data._id || data.id}`}
              className="va-btn va-btn--primary"
            >
              📋 Details
            </Link>
          )}
          
          {/* Share button always available */}
          <button
            className="va-btn va-btn--ghost"
            onClick={() => setOpenShare(true)}
          >
            Share
          </button>
        </div>
      </article>

      {/* Share Modal */}
      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        message={shareMessage}
        url={`${window.location.origin}/tournaments${data.id ? `#${data.id}` : ''}`}
      />
      
      {/* Notify Modal */}
      <NotifyModal
        open={openNotify}
        onClose={() => setOpenNotify(false)}
        tournament={data}
      />
    </>
  );
}
